import { createRootRoute, HeadContent, Outlet, Scripts, useLocation } from "@tanstack/react-router"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useState } from "react"
import { OfflineBanner, PWA } from "@mylaunch/pwa/components"
import appCss from "~/styles/app.css?url"
import { LoginDialog } from "~/features/auth/components/login-dialog"
import { PlansDrawer } from "~/features/plans/components/plans-drawer"
import { PlanApplicationSheet } from "~/features/plans/components/plan-application-sheet"
import { PushPromptDrawer } from "~/features/notifications/components/push-prompt-drawer"
import { QueryBoundary } from "~/components/query-boundary"
import { useSwNavigateBridge } from "~/features/notifications/lib/sw-navigate-bridge"
import { isLoggedIn } from "~/features/auth/server/auth.functions"
import { getCurrentUser, getUnreadCount } from "~/features/profile/server/profile.functions"
import { Suspense } from 'react'
import { Toaster } from "~/components/ui/sonner"

export const Route = createRootRoute({
  ssr: true,
  loader: async () => {
    const loggedIn = await isLoggedIn().catch(() => false)

    return {
      loggedIn,
      currentUserPromise: loggedIn ? getCurrentUser() : Promise.resolve(undefined),
      unreadCountPromise: loggedIn ? getUnreadCount() : Promise.resolve(0),
    }
  },
  head: () => ({
    title: "Платформа для женщин",
    meta: [
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover",
      },
      {
        name: "description",
        content:
          "GWService - это международная информационная платформа для современных женщин, объединяющая партнеров и экспертов в разных уголках мира и предоставляющая доступ к самой полезной информации. Это ваш верный помощник в поиске уникальных событий, экспертных советов и людей с похожими интересами.",
      },
      {
        name: "apple-mobile-web-app-capable",
        content: "yes",
      },
      {
        name: "theme-color",
        content: "#ffffff",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
        blocking: "render"
      },
      {
        rel: "manifest",
        href: "/api/pwa/manifest",
        crossOrigin: "use-credentials",
      },
      {
        rel: "apple-touch-icon",
        href: "/icon-192x192.png",
      },
    ],
    scripts: import.meta.env.PROD ? [{
      children: `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();for(var j=0;j<document.scripts.length;j++){if(document.scripts[j].src===r){return;}}k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window,document,'script','https://mc.yandex.ru/metrika/tag.js?id=108769016','ym');ym(108769016,'init',{ssr:true,webvisor:true,clickmap:true,ecommerce:"dataLayer",accurateTrackBounce:true,trackLinks:true,defer:true});`,
    }] : [],
  }),
  shellComponent: Root,
})

// Routes that stay public even when the user is not logged in.
// Everything else triggers <LoginDialog />.
const PUBLIC_ROUTES = ["/profile/privacy-policy"]

function AuthGate() {
  const pathname = useLocation({ select: (l) => l.pathname })
  if (PUBLIC_ROUTES.includes(pathname)) return null
  return <LoginDialog />
}

function Root() {
  const [queryClient] = useState(() => new QueryClient())
  const data = Route.useLoaderData()
  useSwNavigateBridge()
  return (
    <html>
      <head>
        <HeadContent />
      </head>
      <body className="flex flex-col min-h-dvh" style={{ visibility: "hidden", paddingTop: "var(--offline-banner-h, 0px)" }}>
        {import.meta.env.PROD && (
          <noscript>
            <div><img src="https://mc.yandex.ru/watch/108769016" style={{ position: "absolute", left: "-9999px" }} alt="" /></div>
          </noscript>
        )}
        <OfflineBanner />
        <QueryClientProvider client={queryClient}>
          {!data.loggedIn && <AuthGate />}
          {data.loggedIn && <PushPromptDrawer />}
          {data.loggedIn && (
            <QueryBoundary loadingFallback={null}>
              <PlansDrawer />
            </QueryBoundary>
          )}
          {data.loggedIn && (
            <QueryBoundary loadingFallback={null}>
              <PlanApplicationSheet />
            </QueryBoundary>
          )}
          <Outlet />
        </QueryClientProvider>
        <Suspense>
          <PWA skipInDevMode={false} />
          <Toaster />
        </Suspense>
        <Scripts />
      </body>
    </html>
  )
}
